import {
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemCheckbox,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  type MenuCheckedValueChangeData,
} from "@fluentui/react-components";
import {
  ArrowUndo16Regular,
  CheckmarkCircle16Regular,
  Copy16Regular,
  Delete16Regular,
  Open16Regular,
  Person16Regular,
} from "@fluentui/react-icons";
import { useState, type ReactElement, type ReactNode } from "react";
import { ConfirmDialog, executionTaskDeleteConfirmMessage } from "@/components/app-dialog";
import type { ExecutionPriority, ExecutionStatus, ExecutionTask } from "@/engine/types";
import { PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER } from "@/store/execution-task-ops";
import { AssigneeAvatars } from "./task-ui";
import { priorityGlyph, statusGlyph, taskFieldIcon } from "./task-glyphs";

/** 포털 메뉴의 React 클릭이 카드·행까지 올라가 상세 시트가 열리지 않게 한다. */
function keepClickHere(e: { stopPropagation: () => void }) {
  e.stopPropagation();
}

export interface TaskCommandTarget {
  id: string;
  title: string;
  status?: ExecutionStatus;
  priority?: ExecutionPriority;
  assignees: string[];
}

/** 표·보드·드로어가 같이 쓰는 Task 동작 메뉴. */
export function TaskCommandMenu({
  task,
  canWrite,
  assignees = [],
  onOpen,
  onStatus,
  onPriority,
  onAssignees,
  onDuplicate,
  onDelete,
  deleteNotice,
  extra,
  trigger,
}: {
  task: TaskCommandTarget;
  canWrite: boolean;
  /** 담당 메뉴에 올릴 사람 */
  assignees?: string[];
  onOpen?: () => void;
  onStatus?: (status: ExecutionStatus | undefined) => void;
  onPriority?: (priority: ExecutionPriority | undefined) => void;
  onAssignees?: (names: string[]) => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  deleteNotice?: ReactNode;
  /** 삭제 바로 앞에 두는 그 화면만의 항목 */
  extra?: ReactNode;
  trigger: ReactElement;
}) {
  const [confirm, setConfirm] = useState(false);
  const people = [...new Set([...assignees, ...task.assignees])];
  const done = task.status === "done";
  const showWrite = canWrite && (onStatus || onPriority || onAssignees);
  const showDanger = canWrite && (onDuplicate || onDelete);

  const onStatusChecked = (_: unknown, data: MenuCheckedValueChangeData) => {
    const next = data.checkedItems[0];
    if (!onStatus || next == null) return;
    onStatus(next === "unset" ? undefined : (next as ExecutionStatus));
  };
  const onPriorityChecked = (_: unknown, data: MenuCheckedValueChangeData) => {
    const next = data.checkedItems[0];
    if (!onPriority || next == null) return;
    onPriority(next === "unset" ? undefined : (next as ExecutionPriority));
  };
  const onAssigneeChecked = (_: unknown, data: MenuCheckedValueChangeData) => {
    if (!onAssignees) return;
    const pickedNone = data.checkedItems.includes("__none");
    const names = data.checkedItems.filter((name) => name !== "__none");
    if (pickedNone && task.assignees.length > 0) onAssignees([]);
    else onAssignees(names);
  };

  return (
    <>
      <Menu>
        <MenuTrigger disableButtonEnhancement>{trigger}</MenuTrigger>
        <MenuPopover onClick={keepClickHere}>
          <MenuList>
            {onOpen && (
              <MenuItem icon={<Open16Regular />} onClick={onOpen}>
                열기
              </MenuItem>
            )}
            {canWrite && onStatus && (
              <MenuItem
                icon={done ? <ArrowUndo16Regular /> : <CheckmarkCircle16Regular />}
                onClick={() => onStatus(done ? "notStarted" : "done")}
              >
                {done ? "완료 취소" : "완료"}
              </MenuItem>
            )}
            {showWrite && <MenuDivider />}
            {canWrite && onStatus && (
              <Menu checkedValues={{ status: [task.status ?? "unset"] }} onCheckedValueChange={onStatusChecked}>
                <MenuTrigger disableButtonEnhancement>
                  <MenuItem icon={statusGlyph(task.status, 16, true)}>상태</MenuItem>
                </MenuTrigger>
                <MenuPopover onClick={keepClickHere}>
                  <MenuList>
                    {STATUS_ORDER.map((status) => (
                      <MenuItemRadio key={status} name="status" value={status} icon={statusGlyph(status, 16, true)}>
                        {STATUS_LABEL[status]}
                      </MenuItemRadio>
                    ))}
                    <MenuItemRadio name="status" value="unset" icon={statusGlyph(undefined, 16, true)}>
                      상태 미정
                    </MenuItemRadio>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
            {canWrite && onAssignees && (
              <Menu
                checkedValues={{ assignee: task.assignees.length ? task.assignees : ["__none"] }}
                onCheckedValueChange={onAssigneeChecked}
              >
                <MenuTrigger disableButtonEnhancement>
                  <MenuItem icon={taskFieldIcon("assignee")}>담당</MenuItem>
                </MenuTrigger>
                <MenuPopover onClick={keepClickHere}>
                  <MenuList>
                    {people.map((name) => (
                      <MenuItemCheckbox key={name} name="assignee" value={name} icon={<AssigneeAvatars names={[name]} size={16} max={1} />}>
                        {name}
                      </MenuItemCheckbox>
                    ))}
                    <MenuItemCheckbox name="assignee" value="__none" icon={<Person16Regular />}>
                      담당 미정
                    </MenuItemCheckbox>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
            {canWrite && onPriority && (
              <Menu checkedValues={{ priority: [task.priority ?? "unset"] }} onCheckedValueChange={onPriorityChecked}>
                <MenuTrigger disableButtonEnhancement>
                  <MenuItem icon={priorityGlyph(task.priority)}>우선순위</MenuItem>
                </MenuTrigger>
                <MenuPopover onClick={keepClickHere}>
                  <MenuList>
                    {PRIORITY_ORDER.map((priority) => (
                      <MenuItemRadio key={priority} name="priority" value={priority} icon={priorityGlyph(priority)}>
                        {PRIORITY_LABEL[priority]}
                      </MenuItemRadio>
                    ))}
                    <MenuItemRadio name="priority" value="unset" icon={priorityGlyph(undefined)}>
                      미정
                    </MenuItemRadio>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
            {extra}
            {showDanger && <MenuDivider />}
            {canWrite && onDuplicate && (
              <MenuItem icon={<Copy16Regular />} onClick={onDuplicate}>
                복제
              </MenuItem>
            )}
            {canWrite && onDelete && (
              <MenuItem icon={<Delete16Regular />} onClick={() => setConfirm(true)}>
                삭제
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
      {onDelete && (
        <ConfirmDialog
          open={confirm}
          title="Task 삭제"
          message={
            <>
              {executionTaskDeleteConfirmMessage(1)}
              {deleteNotice ? <> {deleteNotice}</> : null}
            </>
          }
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            setConfirm(false);
            onDelete();
          }}
        />
      )}
    </>
  );
}

export function taskMenuTarget(task: Pick<ExecutionTask, "id" | "title" | "status" | "priority" | "assignees">): TaskCommandTarget {
  return { id: task.id, title: task.title, status: task.status, priority: task.priority, assignees: task.assignees };
}
